/* museum-data.js — the Gallery page's content, lifted out of the 10,658-line
 * three.js file it used to live inside.
 *
 * It began as Cody's, and most of the numbers are still his: the marker UVs
 * were placed by eye against the engraving (a fitted projection drifted badly
 * at the edges — it put Japan out in the Pacific), the city UVs were read off
 * the drawn coastline for the same reason, and the bearings are the angles a
 * country's cities take when it unfolds. K24 moved the places that were off
 * their coast, and Springville went on the same way (see STOPS). The pre-lit
 * sheet was baked through an orthographic camera looking straight down, so it
 * keeps the mesh's UV space exactly and every one of these coordinates still
 * lands where it landed in 3D.
 *
 * The Photo Caches are real photographs since V3.0 (Josh, 2026-09-29): San
 * Diego 8, Springville 8, Tokyo 10 and Rome 12, hung in the order Josh set,
 * each exported from the Drive at 1600 px on the long edge as
 * museum-photo-<place>-NN.webp. Nagoya and Paestum are off the chart until
 * they have photographs of their own, and Utah is on it as Springville, with
 * a Photo Cache and no Film Signal yet. Until Josh approves copy, a cache's
 * title is its place and it has no date. The captions are Claude's drafts, each
 * signed off by Josh on the launch review page (2026-09-29).
 */
window.MUSEUM_DATA = (function () {
  'use strict';

  var A = window.MUSEUM_ASSETS || {};
  function asset(name) { return A[name] || ''; }

  /* The one stand-in picture, for a card with no thumbnail of its own. Since
     V3.0 no card falls through to it (every journey place has a CITY_THUMB and
     every season its cover), but thumbFor keeps it as the last resort. The
     photos() generator that hung it in every frame went with the real
     photographs. */
  var PLACEHOLDER_IMAGE = asset('museum-placeholder-photo.webp');

  /* ---- the places ---------------------------------------------------------
     The journey's four places in the order Josh set (V3.0, 2026-09-29): San
     Diego, Springville, Tokyo, Rome. Then the Dead Warrior Society's six in
     season order (V2.72 K24). A season is a place's entry like a Photo Cache
     is, so the map, the atlas and the tooltips treat every place the same way.
     This order is the atlas's too: it lists the places in it and numbers them
     by it, 01–04 the journey and 05–10 the Society.
     `country` indexes REGIONS; `chartUv` is the place on the sheet; `angle` is
     degrees clockwise from north, the bearing it takes when its country
     unfolds (only a fallback now: every place has its own `chartUv`). `desc`
     is Cody's stand-in text, and nothing on the page reads it.
     `frames` (V3.0, Josh, 2026-09-29) names the place's own set of picture
     frames, a key of FRAME_SETS in museum-hang.js: Josh wanted each place
     hung in frames that belong to it. A place without one hangs in the house
     frames.

     Every `chartUv` was checked against the sheet itself (2026-09-21). Real
     coastlines were laid over the chart and fitted region by region, because
     the engraving is not one clean projection: its Gulf coast and Florida sit
     about 0.016 north of a Mercator fit, and southern Africa about 0.026
     south. Each place was then read off its own region's coast.
     San Diego and Rome were already right and stay Cody's. Tokyo moved to the
     bay head the coast puts it at. Each of the six seasons moved off the plain
     fit, which had put Snow's Island in the Atlantic.

     V3.0 (Josh, 2026-09-29): Nagoya and Paestum are off the chart until they
     have photographs (their checked points are in this file as of K24,
     5a942fc), and every place after them moved up. Springville, Utah went in
     second.
     Springville (40.165 N, 111.611 W) was read the K24 way. The plain fit puts
     it at 0.1713, 0.2959, and the Great Basin is blank on the sheet (there is
     no Great Salt Lake to check it against), so it is read off the coast and
     the places already checked around it. East–west: from 38 to 42.5° N the
     drawn Pacific coast sits 0.0026 east of the fit, and Lake George 0.0040
     east at 43° N, while at 33° N San Diego and the southern California coast
     sit 0.004–0.005 west; at 40° that reads +0.0022. North–south: the West
     sits close to the fit (San Diego 0.0016 north of it, the southern
     California bend on it) and the East well north (Lake George 0.012,
     Snow's Island 0.021); 111° W is a seventh of the way from San Diego to
     them, so −0.0024. That puts Springville at 0.1735, 0.2935, in Utah. */
  var STOPS = [
    { name: 'San Diego', region: 'United States', country: 0, angle: 0, chartUv: [0.1525, 0.3480],
      desc: 'Where the trip began. Early product shots and travel-day imagery of the cases heading out the door.',
      frames: 'san-diego',
      photos: [
        { src: asset('museum-photo-san-diego-01.webp'), caption: 'Hades on the sea wall, the skyline across the bay.' },
        { src: asset('museum-photo-san-diego-02.webp'), caption: 'Theseus, carried along the waterfront.' },
        { src: asset('museum-photo-san-diego-03.webp'), caption: 'Standing still while the street moves.' },
        { src: asset('museum-photo-san-diego-04.webp'), caption: 'Hades, laid down on the pavement.' },
        { src: asset('museum-photo-san-diego-05.webp'), caption: 'Kitted up beside the cases, after dark.' },
        { src: asset('museum-photo-san-diego-06.webp'), caption: 'Loading out of Hades.' },
        { src: asset('museum-photo-san-diego-07.webp'), caption: 'Theseus open, hands on the rifle.' },
        { src: asset('museum-photo-san-diego-08.webp'), caption: 'Theseus open on the stone, packed.' }
      ] },
    { name: 'Springville', region: 'United States', country: 0, chartUv: [0.1735, 0.2935],
      frames: 'springville',
      photos: [
        { src: asset('museum-photo-springville-01.webp'), caption: 'Theseus in the room.' },
        { src: asset('museum-photo-springville-02.webp'), caption: 'Hades in the room.' },
        { src: asset('museum-photo-springville-03.webp'), caption: 'The pair, between two busts.' },
        { src: asset('museum-photo-springville-04.webp'), caption: 'Both cases, from above.' },
        { src: asset('museum-photo-springville-05.webp'), caption: 'Hades, packed.' },
        { src: asset('museum-photo-springville-06.webp'), caption: 'The loadout, laid out.' },
        { src: asset('museum-photo-springville-07.webp'), caption: 'Theseus on the Wall Mount.' },
        { src: asset('museum-photo-springville-08.webp'), caption: 'Hades and the bust.' }
      ] },
    { name: 'Tokyo', region: 'Japan', country: 1, angle: 35, chartUv: [0.8356, 0.3199],
      desc: 'First landing. The bulk of the big urban imagery came from here, along with most of the shrine work across the eastern wards.',
      frames: 'tokyo',
      photos: [
        { src: asset('museum-photo-tokyo-01.webp'), caption: 'Hades, wheeled through Shinjuku.' },
        { src: asset('museum-photo-tokyo-02.webp'), caption: 'Into Omoide Yokocho, under the lanterns.' },
        { src: asset('museum-photo-tokyo-03.webp'), caption: 'A rest in the alley, the cases standing by.' },
        { src: asset('museum-photo-tokyo-04.webp'), caption: 'The cases in a temple gate, between two lanterns.' },
        { src: asset('museum-photo-tokyo-05.webp'), caption: 'Against the timber wall.' },
        { src: asset('museum-photo-tokyo-06.webp'), caption: 'Hades on the move, the city behind.' },
        { src: asset('museum-photo-tokyo-07.webp'), caption: 'Hades, standing in the alley.' },
        { src: asset('museum-photo-tokyo-08.webp'), caption: 'Waiting under the lantern.' },
        { src: asset('museum-photo-tokyo-09.webp'), caption: 'Theseus on a ledge, blossom beside it.' },
        { src: asset('museum-photo-tokyo-10.webp'), caption: 'Through the subway passage.' }
      ] },
    { name: 'Rome', region: 'Italy', country: 2, angle: -20, chartUv: [0.5045, 0.2825],
      desc: 'The imagery here speaks for itself — the city is unmistakable in every frame.',
      frames: 'rome',
      photos: [
        { src: asset('museum-photo-rome-01.webp'), caption: 'Past the Colosseum, the sun low.' },
        { src: asset('museum-photo-rome-02.webp'), caption: 'Holding the crossing while the travelers pass.' },
        { src: asset('museum-photo-rome-03.webp'), caption: 'Past the Antica Salumeria.' },
        { src: asset('museum-photo-rome-04.webp'), caption: 'Theseus, carried up the steps.' },
        { src: asset('museum-photo-rome-05.webp'), caption: 'Theseus before the Arch of Titus.' },
        { src: asset('museum-photo-rome-06.webp'), caption: 'Theseus under the colonnade.' },
        { src: asset('museum-photo-rome-07.webp'), caption: 'Theseus at the foot of a column.' },
        { src: asset('museum-photo-rome-08.webp'), caption: 'Hades at the foot of a column.' },
        { src: asset('museum-photo-rome-09.webp'), caption: 'Both cases at the foot of a column.' },
        { src: asset('museum-photo-rome-10.webp'), caption: 'Theseus mounted on Hades, on the terrace.' },
        { src: asset('museum-photo-rome-11.webp'), caption: 'At the foot of the Vittoriano.' },
        { src: asset('museum-photo-rome-12.webp'), caption: 'Theseus in hand.' }
      ] },
    /* 4–9, the Dead Warrior Society's places, in season order: I to VI. */
    { name: 'Sparta', region: 'Greece', country: 5, chartUv: [0.5256, 0.3093], photos: [] },
    { name: 'Lake George', region: 'United States', country: 0, chartUv: [0.2752, 0.2585], photos: [] },
    { name: 'Burgos', region: 'Spain', country: 4, chartUv: [0.4577, 0.2661], photos: [] },
    { name: 'Snow’s Island', region: 'United States', country: 0, chartUv: [0.2528, 0.3210], photos: [] },
    { name: 'Mexico City', region: 'Mexico', country: 3, chartUv: [0.1905, 0.4285], photos: [] },
    { name: 'Harare', region: 'Zimbabwe', country: 6, chartUv: [0.5548, 0.6809], photos: [] }
  ];

  /* ---- the countries: the atlas's PLACE row and the phone's stepper ------
     Every country with a place in it is a PLACE (Josh, K24: "each location
     needs to be a place option in the top header"). REGIONS stays
     index-aligned with every place's `country`, so the first three keep their
     numbers and the Society's four countries follow. V3.0 took no country
     away (Japan and Italy keep one place each) and added none (Springville is
     the United States' fourth), so these numbers did not move; only the
     places listed under them did. */
  var REGIONS = [
    { name: 'United States', stops: [0, 1, 5, 7] },
    { name: 'Japan', stops: [2] },
    { name: 'Italy', stops: [3] },
    { name: 'Mexico', stops: [8] },
    { name: 'Spain', stops: [6] },
    { name: 'Greece', stops: [4] },
    { name: 'Zimbabwe', stops: [9] }
  ];
  /* The row and the stepper read west to east, the way the chart does. */
  var REGION_TAB_ORDER = [0, 3, 4, 2, 5, 6, 1];

  /* ---- the markers (V2.72 K24) ------------------------------------------
     One marker per place. K24 let two genuinely close places share one that
     unfolds into both when its country is chosen and the zoom has room (Josh:
     "utilize the subicon feature in italy and japan for locations that are
     genuinely close. otherwise the location needs its own hotspot"). Italy
     (Rome and Paestum) and Japan (Tokyo and Nagoya) were the two; since V3.0
     each holds one place and is that place's own marker, standing on it. A
     shared marker is still one line here (`stops` of two, a `name` and a
     `uv`), and museum-chart.js still folds and unfolds it.
     A marker with no `uv` of its own stands on its place.
     `offset` is in screen pixels at home, for the four markers that would
     otherwise sit on each other there: Springville off San Diego (V3.0),
     Lake George off Snow's Island, and Sparta off Rome. It fades out by 3×
     home, where every marker stands on its true point. It never carries a
     marker so far that its true point leaves the ring. */
  var STATIONS = [
    { stops: [0] },                                                           /* San Diego */
    { stops: [1], offset: [6, -9] },                                          /* Springville */
    { stops: [5], offset: [5, -6] },                                          /* Lake George */
    { stops: [7], offset: [-5, 7] },                                          /* Snow’s Island */
    { stops: [8] },                                                           /* Mexico City */
    { stops: [6] },                                                           /* Burgos */
    { stops: [3] },                                                           /* Rome */
    { stops: [4], offset: [16, 16] },                                         /* Sparta */
    { stops: [9] },                                                           /* Harare */
    { stops: [2] }                                                            /* Tokyo */
  ];
  var STOP_STATION = [];
  STATIONS.forEach(function (st, i) {
    var first = STOPS[st.stops[0]];
    st.index = i;
    st.country = first.country;
    st.uv = st.uv || first.chartUv;
    st.name = st.name || first.name;
    st.stops.forEach(function (r) { STOP_STATION[r] = i; });
  });
  REGIONS.forEach(function (r, i) {
    r.stations = STATIONS.filter(function (st) { return st.country === i; })
      .map(function (st) { return st.index; });
  });

  /* How deep a country is framed when it opens, as a multiple of home. The
     frame itself is fitted to the country's markers at open, so a country
     with places far apart comes in shallower on a narrow screen. The 3D page
     carried the first three as camera radii (2.20 / 2.05 / 1.35 against a
     home of 7.4); these are 7.4 / radius. Japan went as deep as Italy in K25
     (was 3.61) so Tokyo and Nagoya could part; with Tokyo alone since V3.0 it
     stays there, so Japan and Italy still open alike. */
  var REGION_VIEW = [
    { zoom: 3.364 },   /* United States */
    { zoom: 5.481 },   /* Japan */
    { zoom: 5.481 },   /* Italy */
    { zoom: 4.0 },     /* Mexico */
    { zoom: 5.0 },     /* Spain */
    { zoom: 5.0 },     /* Greece */
    { zoom: 4.2 }      /* Zimbabwe */
  ];

  /* The close-up sheets: none, since V2.72 K24. Japan's (museum-sheet-region-1)
     is a separate render of Asia, letterboxed into the sheet's proportions,
     so its Japan sits near u 0.62 while the world's is near 0.83. Laid over
     the world sheet it showed open paper where Japan should be (Josh: "zooming
     on japan ... opens a blank part of the map"). Japan zooms the world sheet
     now, as Italy always has. Region 0's close-up was never shipped: its
     source art is an unlicensed iStock comp with the watermark burned in
     (id 854984638). The chart still takes a sheet per region if one is ever
     baked to the world's own UV space. */
  var REGION_SHEET = {};

  /* ---- the catalogue ----------------------------------------------------
     `chip` is the name on the atlas's MEDIA row, where three full names
     would not fit one row of a 380px column; the card and the chip's own
     aria-label keep the full name (V2.72 K14). */
  var MEDIA_KINDS = {
    photo:   { label: 'Photo Cache',          chip: 'Photo Cache', ink: '#8EA6B3', badge: '#3E5A66' },
    society: { label: 'Dead Warrior Society', chip: 'DWS',         ink: '#D8564A', badge: '#7E1A16' },
    film:    { label: 'Film Signal',          chip: 'Films',       ink: '#7FA17D', badge: '#3D5A3B' }
  };

  var MEDIA_THUMB = {
    nagoyaFilm: asset('museum-media-thumb-nagoya-film.webp'),
    sanDiegoFilm: asset('museum-media-thumb-san-diego-film.webp')
  };
  /* A journey place's card picture, keyed by its STOPS index. Each is cut
     from a photograph hanging on that place's wall (V3.0, Josh 2026-09-30),
     760 wide: San Diego 03, Springville 01, Tokyo 05, Rome 01. */
  var CITY_THUMB = {
    0: asset('museum-city-thumb-san-diego.webp'),     /* the platform */
    1: asset('museum-city-thumb-springville.webp'),   /* the case in the gallery */
    2: asset('museum-city-thumb-tokyo.webp'),         /* the timber wall */
    3: asset('museum-city-thumb-rome.webp')           /* the Colosseum */
  };
  var KIND_THUMB = { society: asset('museum-kind-thumb-society.webp') };

  /* What each place holds, index-aligned with STOPS. One Photo Cache per
     journey place. V2.72 K21 (Josh): the two Film Signals and the two Dead
     Warrior Society videos are out. There are no Film Signals yet (Utah's is
     still to come; Springville has its Photo Cache only), and DWS came back
     as its seasons, one per Society place (4–9), added below with the
     seasons themselves. Every kind stays defined above, with its thumbnails,
     as the template for what comes next: an entry of any kind is one line in
     its place's list.
     V3.0 (Josh, 2026-09-29): a cache is titled with its place and has no date
     until copy is approved. Cody's stand-in titles and dates are gone. San
     Diego's keeps "Chariot Bundle Photoshoot", because that title is Josh's
     own (K21). An entry with no date carries an empty one (see MEDIA_FLAT). */
  var MEDIA = [
    [ /* 0 — San Diego */
      { kind: 'photo',   title: 'Chariot Bundle Photoshoot' }
    ],
    [ /* 1 — Springville */
      { kind: 'photo',   title: 'Springville' }
    ],
    [ /* 2 — Tokyo */
      { kind: 'photo',   title: 'Tokyo' }
    ],
    [ /* 3 — Rome */
      { kind: 'photo',   title: 'Rome' }
    ]
  ];

  var PLACE_LABEL = ['San Diego, CA', 'Springville, UT', 'Tokyo, Japan', 'Rome, Italy',
    'Sparta, Greece', 'Lake George, NY', 'Burgos, Spain', 'Snow’s Island, SC', 'Mexico City, Mexico',
    'Harare, Zimbabwe'];

  /* ---- Dead Warrior Society — Zach's channel, as seasons (V2.72 K22) -----
     Six seasons, numbered in release order (Josh, 2026-09-21), each pinned to
     the ground its story was fought over. The channel's playlists are close to
     seasons but not quite:
       - the Conquistadors playlist stops before the six Cortés episodes, which
         live in a podcast playlist;
       - Rhodesia only has a podcast playlist;
       - the Rangers playlist ends on Zach's two modern Special Forces epilogues.
     V joins the first two, and II keeps the epilogues, flagged. Left off on
     purpose: the other Special Forces commentaries, the trailer, three general
     videos and every Short. A new episode is one line in its season.

     `stop` is the season's place in STOPS (4–9). The place carries the map
     point and the country; since K24 a season is that place's entry, and is
     marked, filtered and counted the way a Photo Cache is: one season, one
     entry. `place` is the full name the season view and the player print.
     Episodes are [YouTube id, title, running time, flag]. */
  var DWS_CHANNEL = 'https://www.youtube.com/@DeadWarriorSociety';
  var DWS_SEASONS = [
    { no: 'I', key: 'sparta', title: 'Spartan Institutions and Hegemony', sub: '',
      place: 'Sparta, Greece', stop: 4,
      playlist: 'PLg81qxl9FWzh0gbJOSYVZWu4jLi_31Zbo', cover: 'XkUcnImuTa8',
      episodes: [
        ['XkUcnImuTa8', 'Spartan Origins: The Return of the Heracleidae and the Laconian Conquest', '20:18'],
        ['wmlxXcRemNc', 'Spartan Origins: The Spartan Conquest of Messenia', '52:04'],
        ['5gZH3Zgjbnc', 'The Spartan Agoge, Part I: Fitness, Discipline, and Obedience', '39:33'],
        ['dQWJvJ-4ylU', 'The Spartan Agoge, Part II: The Myth of Spartan Pederasty', '1:02:07'],
        ['Rlru-6h_FQw', 'The Krypteia: Sparta’s Teenage Asymmetrical Warriors', '29:38'],
        ['uwVWUz5oLJE', 'Cleomenes I: The Schizophrenic Sociopathic Spartan King', '1:07:13'],
        ['wAhS173LRVQ', 'Corruption, Betrayal, and Suicide: The Demise of King Cleomenes I', '47:05'],
        ['lv8rDunIfTo', 'The Rogue Spartan: Brasidas’s Legacy, Part I', '30:51'],
        ['zNe96o3j1r8', 'Sparta’s Guerrilla War Against Athens: Brasidas’s Legacy, Part II', '1:03:40'],
        ['7-mPSYogAyQ', 'Sparta’s Revenge at Amphipolis: Brasidas’s Legacy, Part III', '48:40']
      ] },
    { no: 'II', key: 'rangers', title: 'Tactics of the Wilderness', sub: 'Robert Rogers and His Rangers',
      place: 'Lake George, New York', stop: 5,
      playlist: 'PLg81qxl9FWzgynVrwCAntdjrvC088IVb9', cover: '9Ss39x41Rx4',
      episodes: [
        ['cadksT0UPYw', 'The Rise of European Asymmetrical Warfare in North America', '26:31'],
        ['9Ss39x41Rx4', 'History’s First Tier 1 Unit: Robert Rogers and the Evolution of His Rangers', '32:25'],
        ['BrYBFVdaRVo', 'Guerrilla Warfare in North America: Robert Rogers Activates His Rangers', '51:20'],
        ['6S5UMBVFVxk', 'The Most Formidable Tribe in the New World: The Abenaki', '40:33'],
        ['U7vhrieMwe0', 'Take Your Revenge: The Rangers Prepare for the Raid on St. Francis', '50:36'],
        ['J3tU16aDOLc', 'The Rangers Invade Canada and the Battle on Snowshoes', '58:59'],
        ['NsgZo06cJ5I', 'Raid on St. Francis: Rogers and His Rangers Destroy St. Francis', '1:07:36'],
        ['mUofYTZ4cWY', 'Robert Rogers: Hero or Monster?', '35:14'],
        ['G2TkVP6wQxA', 'The Slow Death of U.S. Army Special Forces', '26:31', 'Epilogue'],
        ['zU7fRI-nQzM', 'Crisis in US Army Special Forces?', '11:34', 'Epilogue']
      ] },
    { no: 'III', key: 'spain', title: 'The Reconquest of Spain', sub: '',
      place: 'Burgos, Spain', stop: 6,
      playlist: 'PLg81qxl9FWziAc8cVs5asiO79Ih33JYr3', cover: '1vc20LV2v5o',
      episodes: [
        ['1vc20LV2v5o', 'Shock and Awe: The Islamic Invasion and Conquest of Visigothic-Roman Spain', '52:16'],
        ['6A4sw--ez9M', 'The Rise and Fall of the Umayyad Dynasty in Al-Andalus', '1:34:09'],
        ['ThMneUFe-Bw', 'The First True Crusaders: Spain’s Relentless War Against Islam and Al-Andalus', '43:13'],
        ['5fiwcUcPANE', 'El Cid’s Early Career and the Spanish Medieval World', '1:17:44'],
        ['CQ16p4zub-k', 'The Lion of Castile: El Cid’s Path to Power', '1:04:49'],
        ['z1rFHYhF_lo', 'Islam Strikes Back: El Cid in Exile and the Invasion of the Almoravids', '58:54'],
        ['m8P3NQ_iGak', 'El Cid: At War With Everyone', '1:21:13']
      ] },
    { no: 'IV', key: 'swampfox', title: 'The Swamp Fox', sub: '',
      place: 'Snow’s Island, South Carolina', stop: 7,
      playlist: 'PLg81qxl9FWzjSzzUICbFvIecH1IXNh-xu', cover: 'fPusd3fSFh0',
      episodes: [
        ['fPusd3fSFh0', 'The Birth of the Swamp Fox: The Early Life and Exploits of Francis Marion', '1:12:40'],
        ['TZiIETPsaUs', 'First Strike: The Swamp Fox and His Guerrillas Raid Sumter’s Plantation', '44:25'],
        ['OCkN40u-QFE', 'The Master of Chaos: Francis Marion’s Raid at Black Mingo and Tearcoat Swamp', '55:38'],
        ['51jgnF7pqQI', 'Swamp Fox vs. Bloody Ban: Counter-Insurgency Lessons from 1780', '23:15'],
        ['wumpGuM-IWw', 'The Swamp Fox and His Guerrillas Turn Pro', '36:11'],
        ['VqJEKURF0Jg', 'Improvised Siege Warfare: The Swamp Fox Takes the Forts', '32:34'],
        ['-nqA5gMCAIY', 'The Swamp Fox vs. The Ranger: Lessons for the Modern Warfighter', '39:55', 'Crossover']
      ] },
    { no: 'V', key: 'mexico', title: 'Conquistadors and the Invasion of the Americas',
      sub: 'Hernán Cortés and the Conquest of Mexico',
      place: 'Mexico City, Mexico', stop: 8,
      playlist: 'PLg81qxl9FWzi4tB0nEgbNd5OzZto6DXb2', cover: 'YQJoGCB82Zc',
      episodes: [
        ['YQJoGCB82Zc', 'Conquistadors: Soldiers of Fortune and God', '1:02:22'],
        ['abhv_pAmWbg', 'The Aztec War Machine: Honor, Empire, and Human Sacrifice', '1:16:05'],
        ['k4VLUbMRB4g', 'The Conquistador’s Ranger Handbook and UW Manual', '40:46'],
        ['3ePNx_EYxaQ', 'The Aztec Army: How the Mexica Came to Dominate Mesoamerica', '45:41'],
        ['bwJmxEoeRWc', 'A Special Forces Analysis of the Conquest of Mexico', '51:12'],
        ['Z7ND3rMmayc', 'The Maya Campaign: Cortés Invades the Yucatán Peninsula', '47:36'],
        ['3o9VRsQ7vK0', 'Cortés and the Spaniards Come Face to Face with the Aztec', '44:51'],
        ['cCv85JNVLlY', 'Why Cortés Destroyed His Own Ships', '59:27'],
        ['KhTye4boO_o', 'They Tried to Eat the Conquistadors', '42:25'],
        ['VeBgq0w8leg', 'Why Did the Aztec Empire Surrender to 500 Conquistadors?', '43:06']
      ] },
    { no: 'VI', key: 'rhodesia', title: 'Rhodesia', sub: '',
      place: 'Harare, Zimbabwe', stop: 9,
      playlist: 'PLg81qxl9FWzgonSBNSGyB1PHXMx1JU1Hl', cover: 'RqICpkl7eBA',
      episodes: [
        ['RqICpkl7eBA', 'There Will Be Blood: Cecil Rhodes and the Birth of Rhodesia', '56:48'],
        ['uHeiC0iUUHU', 'Rhodesia’s Descent into the Bush War', '21:51'],
        ['uwzzU-dLzvo', 'Detect, Deploy, & Destroy: The Rhodesian Light Infantry and Fire Force', '1:00:21'],
        ['Bdepz6BX6D4', 'Outnumbered 44:1: The Rhodesian SAS and Operation Dingo, Part 1', '49:16'],
        ['6dK3NA88_dE', 'Operation Dingo: The Raid That Nearly Won the War? Part 2', '44:35']
      ] }
  ];

  /* YouTube keeps every size of every thumbnail at a fixed address. mq is
     320 × 180 and always exists; maxres is the cover, and hq is its fallback
     for the rare upload without one. Nothing is copied into the theme: the
     pictures are Zach's and follow any change he makes to them. */
  function ytThumb(id, size) {
    return 'https://i.ytimg.com/vi/' + id + '/' + (size || 'mqdefault') + '.jpg';
  }
  function episodesLabel(n) { return n + (n === 1 ? ' episode' : ' episodes'); }
  /* "6 h 41 m" from the episodes' own running times. */
  function runtimeLabel(eps) {
    var s = 0;
    eps.forEach(function (e) {
      s += String(e[2]).split(':').reduce(function (t, p) { return t * 60 + Number(p || 0); }, 0);
    });
    var h = Math.floor(s / 3600), m = Math.round((s % 3600) / 60);
    if (m === 60) { h += 1; m = 0; }
    return h ? (h + ' h ' + m + ' m') : (m + ' m');
  }
  /* A season is its place's entry. Its card's kind line carries its number,
     and its second line the episodes where a Photo Cache's would carry its
     date. */
  DWS_SEASONS.forEach(function (s, i) {
    s.index = i;
    s.runtime = runtimeLabel(s.episodes);
    s.playlistUrl = s.playlist ? 'https://www.youtube.com/playlist?list=' + s.playlist : DWS_CHANNEL;
    MEDIA[s.stop] = [{
      kind: 'society',
      kindLabel: MEDIA_KINDS.society.label + ' · Season ' + s.no,
      date: episodesLabel(s.episodes.length),
      title: s.title,
      thumb: ytThumb(s.cover),
      season: s
    }];
  });

  /* Flattened once, because the panel and the chart iterate the same list and a
     card must always be able to find its marker and the reverse. The key leads
     with the place, because that is what has to be parsed back out of it. A
     missing date becomes an empty string here, so no view is handed
     `undefined` to print (V3.0: the caches have none). */
  var MEDIA_FLAT = [];
  MEDIA.forEach(function (list, r) {
    (list || []).forEach(function (m, i) {
      MEDIA_FLAT.push({
        key: r + ':' + i,
        cityKey: 'city:' + r,
        stop: r,
        region: STOPS[r].country,
        index: i,
        kind: m.kind,
        kindLabel: m.kindLabel || MEDIA_KINDS[m.kind].label,
        date: m.date || '',
        title: m.title,
        thumb: m.thumb || null,
        place: PLACE_LABEL[r],
        season: m.season || null
      });
    });
  });

  /* Entries per region, for the stepper. A season is one entry, like a Photo
     Cache (Josh, K24: "a dws season is 1 dws"). */
  var MEDIA_COUNTS = REGIONS.map(function (r, i) {
    return MEDIA_FLAT.filter(function (m) { return m.region === i; }).length;
  });

  function thumbFor(m) {
    return m.thumb || KIND_THUMB[m.kind] || CITY_THUMB[m.stop] || PLACEHOLDER_IMAGE;
  }

  /* One label, one place: "1 entry" reads wrong as "1 entries" and the panel,
     the rail and the stepper all say it. */
  function entriesLabel(n) { return n + (n === 1 ? ' entry' : ' entries'); }

  var ICON_SVG = {
    photo: '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M3 8h4l1.4-2h7.2L17 8h4v11H3z"/><circle cx="12" cy="13" r="3.4"/></svg>',
    /* The Sigil (Josh, 2026-09-21, off the Society Icon canvas): the DWS mark's
       inverted triangle, open where a solid skull rises through its top edge.
       A line icon like its two siblings, on their 24 grid and 1.6 stroke, with
       the skull as one filled path. It is drawn on the 15 px pixel grid (1.6
       units = 1 px), so the eyes, nose and tooth gap land on whole pixels at
       1x, 2x and 3x: never rescale, re-stroke or outline it. It feeds every
       place the society is drawn: the MEDIA chip, the map tooltip and the
       season view (V2.72 K23). The seals and the atlas's DWS head that also
       carried it went in K24, when a season became a place's entry. */
    society: '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M4.66 4L2 4L12 20.8L22 4L19.34 4"/><path fill="currentColor" stroke="none" fill-rule="evenodd" d="M6.4 7.2A5.6 5.6 0 0 1 17.6 7.2L17.6 7.8L14.4 10.2L14.4 12.8L12.8 12.8L12.8 11.4L11.2 11.4L11.2 12.8L9.6 12.8L9.6 10.2L6.4 7.8ZM8 6.4a1.6 1.6 0 1 0 3.2 0a1.6 1.6 0 1 0 -3.2 0ZM12.8 6.4a1.6 1.6 0 1 0 3.2 0a1.6 1.6 0 1 0 -3.2 0ZM11.2 8.3H12.8L12 9.7Z"/></svg>',
    film: '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><rect x="3" y="7" width="12" height="10" rx="1.5"/><path d="M15 11.5 21 8v8l-6-3.5z"/></svg>',
    pin: '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"><path d="M12 21s7-6.4 7-11a7 7 0 1 0-14 0c0 4.6 7 11 7 11z"/><circle cx="12" cy="10" r="2.4"/></svg>',
    x: '<svg class="x" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>'
  };

  return {
    STOPS: STOPS,
    REGIONS: REGIONS,
    REGION_TAB_ORDER: REGION_TAB_ORDER,
    STATIONS: STATIONS,
    STOP_STATION: STOP_STATION,
    REGION_VIEW: REGION_VIEW,
    REGION_SHEET: REGION_SHEET,
    MEDIA: MEDIA,
    MEDIA_FLAT: MEDIA_FLAT,
    MEDIA_KINDS: MEDIA_KINDS,
    MEDIA_COUNTS: MEDIA_COUNTS,
    PLACE_LABEL: PLACE_LABEL,
    DWS_SEASONS: DWS_SEASONS,
    DWS_CHANNEL: DWS_CHANNEL,
    ytThumb: ytThumb,
    episodesLabel: episodesLabel,
    PLACEHOLDER_IMAGE: PLACEHOLDER_IMAGE,
    ICON_SVG: ICON_SVG,
    thumbFor: thumbFor,
    entriesLabel: entriesLabel,
    sheets: {
      /* Three tiers. 1536 is the phone's, 2560 is everyone's first load, and
         3072 is fetched only when a visitor actually dives into a region,
         where the sheet is shown at 3.4–5.5 × home. The 3D page fetched its
         2.44 MB 4096 sheet at idle for everybody and re-baked its normal maps
         off it; a 4096 bake was measured at 2.12 MB and declined for this. */
      world: {
        src: asset('museum-sheet-world.webp'),
        src1536: asset('museum-sheet-world-1536.webp'),
        srcHi: asset('museum-sheet-world-3072.webp')
      },
      regions: REGION_SHEET
    }
  };
})();
